/**
 * The names a recording's speakers are known by, live, plus the one way to change them.
 *
 * **A subscription of exactly one row beside an imperative pager.** The transcript itself is
 * thousands of blocks behind an activated revision and cannot change while somebody reads it,
 * which is why `useTranscriptDocument` pages it with plain queries. A name is the opposite: a
 * colleague renames a speaker and every turn header, the legend and the timeline have to say so at
 * once. So the map alone is a `useQuery`, and re-paging a five-hour recording to see a rename
 * never happens.
 *
 * The first page already carried a map (`listTranscriptBlocks` returns one), and it is the
 * fallback here: until the subscription resolves, the legend draws the names the transcript
 * arrived with rather than flashing `Speaker N` over a recording somebody named last week.
 *
 * **Both halves name the transcription being read.** The speaker numbers are that run's, so a
 * subscription keyed on the document alone would label the transcript on screen with another
 * one's names, and a rename sent without it would write them there. Renaming is refused outright
 * once that transcription is no longer what the document reads as: a viewer left open across a
 * re-transcription reloads rather than naming a transcript nobody is looking at.
 */

import { api } from "@repo/convex/api";
import type { Id } from "@repo/convex/dataModel";
import type { TranscriptSpeakerNames } from "@repo/shared";
import { useQuery } from "convex/react";
import { useCallback, useState } from "react";
import { useObservedMutation } from "@/hooks/use-observed-convex";
import { resolveDocumentRefusalMessage } from "../lib/document-processing-view";

export type RenameSpeakerResult = { ok: true } | { ok: false; message: string };

export type UseTranscriptSpeakerNamesResult = {
  speakerNames: TranscriptSpeakerNames;
  /** An empty `name` clears the entry, so the label falls back to `Speaker N`. */
  renameSpeaker: (speaker: number, name: string) => Promise<RenameSpeakerResult>;
  /** A rename this speaker's own label is waiting on. */
  isRenaming: (speaker: number) => boolean;
};

const NO_NAMES: TranscriptSpeakerNames = {};

export function useTranscriptSpeakerNames(
  projectId: Id<"projects">,
  artifactId: string | null,
  /** The transcription being read. `null` until its first page has landed. */
  processingRevisionId: string | null,
  fallback: TranscriptSpeakerNames
): UseTranscriptSpeakerNamesResult {
  const subscribed = useQuery(
    api.documentTranscript.getSpeakerNames,
    artifactId === null || processingRevisionId === null
      ? "skip"
      : {
          projectId,
          artifactId: artifactId as Id<"artifacts">,
          processingRevisionId: processingRevisionId as Id<"artifactProcessingRevisions">,
        }
  );
  const rename = useObservedMutation(api.documentTranscript.renameSpeaker, {
    feature: "documents",
    operation: "rename-transcript-speaker",
    includeRequestId: true,
  });
  const [pending, setPending] = useState<ReadonlySet<number>>(new Set());

  const renameSpeaker = useCallback(
    async (speaker: number, name: string): Promise<RenameSpeakerResult> => {
      if (artifactId === null || processingRevisionId === null) {
        return { ok: false, message: "This recording is no longer open." };
      }
      setPending((current) => new Set(current).add(speaker));
      try {
        await rename({
          projectId,
          artifactId: artifactId as Id<"artifacts">,
          processingRevisionId: processingRevisionId as Id<"artifactProcessingRevisions">,
          speaker,
          name,
        });
        return { ok: true };
      } catch (error) {
        return {
          ok: false,
          message: resolveDocumentRefusalMessage(error, "Couldn't rename this speaker"),
        };
      } finally {
        setPending((current) => {
          const next = new Set(current);
          next.delete(speaker);
          return next;
        });
      }
    },
    [artifactId, processingRevisionId, projectId, rename]
  );

  const isRenaming = useCallback((speaker: number) => pending.has(speaker), [pending]);

  return {
    // `undefined` is the subscription still loading and `null` is a recording that has gone away;
    // the page's own map is the honest answer to both.
    speakerNames: subscribed?.speakerNames ?? fallback ?? NO_NAMES,
    renameSpeaker,
    isRenaming,
  };
}
